import { useCallback, useEffect } from 'react';
import { DateTime } from 'luxon';

import { ONBOARDING_STAGE } from 'src/data/onboarding';
import { lastGeneratedPersonaHintTimestamp } from 'src/lib/localData';
import { createStateSelectorHook } from 'src/lib/sweetState';
import {
  CustomerPersonaStore,
  useCurrentUser,
  useOnboardingActions,
  useUserActions,
} from 'src/store';

import { DocumentViewStore, MainStore } from '../store';

const useMainStore = createStateSelectorHook(MainStore, ['currentVisiblePopup', 'generationTool']);
const useDocumentStore = createStateSelectorHook(DocumentViewStore, ['hasHiddenSuggestedPersonas']);
const useCustomerPersonaState = createStateSelectorHook(CustomerPersonaStore, [
  'loadingSuggestedCustomerPersonas',
]);

export const useHiddenAudiencesOnboardingHint = () => {
  const { currentVisiblePopup, generationTool } = useMainStore();
  const { hasHiddenSuggestedPersonas } = useDocumentStore();
  const { loadingSuggestedCustomerPersonas } = useCustomerPersonaState();
  const userActions = useUserActions();
  const onboardingActions = useOnboardingActions();
  const currentUser = useCurrentUser();
  const toolIsSelected = Boolean(generationTool);

  const wasShownInLast24Hours = useCallback(() => {
    const lastSeen = DateTime.fromMillis(parseInt(lastGeneratedPersonaHintTimestamp.read(0)));
    const currentTimestamp = DateTime.fromMillis(DateTime.utc().ts);
    const differenceInDays = currentTimestamp.diff(lastSeen, ['days']).days;

    return Math.abs(differenceInDays) < 1;
  }, []);

  useEffect(() => {
    if (currentVisiblePopup) {
      onboardingActions.stopOnboarding(ONBOARDING_STAGE.documentSuggestedPersonasNotify);
    }
  }, [currentVisiblePopup, onboardingActions]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    if (!toolIsSelected) {
      onboardingActions.stopOnboarding(ONBOARDING_STAGE.documentSuggestedPersonasNotify);
      return;
    }

    if (onboardingActions.getIsActive()) {
      return;
    }

    if (loadingSuggestedCustomerPersonas) {
      return;
    }

    const isStageFinished = userActions.isFinishedOnboardingStage(
      ONBOARDING_STAGE.documentSuggestedPersonasNotify
    );

    if (isStageFinished) {
      return;
    }

    // We want to display the hint with intervals of 24 hours
    if (wasShownInLast24Hours()) {
      return;
    }

    // We need to wait for the suggested seen ids to be reset after fetch and render
    const timer = setTimeout(() => {
      if (!hasHiddenSuggestedPersonas) {
        return;
      }

      lastGeneratedPersonaHintTimestamp.write(DateTime.utc().ts);

      onboardingActions.startOnboarding(ONBOARDING_STAGE.documentSuggestedPersonasNotify);
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, [
    currentUser,
    hasHiddenSuggestedPersonas,
    loadingSuggestedCustomerPersonas,
    onboardingActions,
    toolIsSelected,
    userActions,
    wasShownInLast24Hours,
  ]);
};
